import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from './ui/dialog';
import { Switch } from './ui/switch';
import { Image as ImageIcon, Plus, Trash2, Edit, Eye, EyeOff, Upload } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BLANK = { title: '', subtitle: '', image_url: '', cta_text: '', cta_link: '', order: 0, active: true };

// Admin CMS for home-page banners. Lets the admin upload an image (base64
// inline), set CTA, control display order, and toggle active state.
export const AdminBannersView = () => {
  const { t } = useLanguage();
  const [banners, setBanners] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    try {
      const r = await axios.get(`${API}/admin/banners`, { withCredentials: true });
      setBanners(r.data || []);
    } catch (e) {
      toast.error(t('فشل التحميل', 'Failed to load', 'Échec'));
    }
  };

  useEffect(() => { reload(); }, []);

  const openCreate = () => { setEditing(null); setForm(BLANK); setOpen(true); };
  const openEdit = (b) => { setEditing(b); setForm({ ...BLANK, ...b }); setOpen(true); };

  const handleImage = (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error(t('الصورة كبيرة جداً (2MB)', 'Image too large (max 2 MB)', 'Image trop volumineuse'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, image_url: reader.result }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/admin/banners/${editing._id}`, form, { withCredentials: true });
      } else {
        await axios.post(`${API}/admin/banners`, form, { withCredentials: true });
      }
      toast.success(t('تم الحفظ', 'Saved', 'Enregistré'));
      setOpen(false);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('فشل الحفظ', 'Save failed', 'Échec'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t('حذف هذه الشريحة؟', 'Delete this banner?', 'Supprimer ?'))) return;
    try {
      await axios.delete(`${API}/admin/banners/${id}`, { withCredentials: true });
      toast.success(t('تم الحذف', 'Deleted', 'Supprimé'));
      reload();
    } catch (e) { toast.error(t('فشل', 'Failed', 'Échec')); }
  };

  const toggleActive = async (b) => {
    try {
      await axios.put(`${API}/admin/banners/${b._id}`, { ...b, active: !b.active }, { withCredentials: true });
      reload();
    } catch (e) { toast.error(t('فشل', 'Failed', 'Échec')); }
  };

  return (
    <div className="space-y-4" data-testid="admin-banners-view">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-[#0d47a1] flex items-center gap-2">
          <ImageIcon size={18} /> {t('شرائح الصفحة الرئيسية', 'Home Banners', 'Bannières')}
        </h3>
        <Button onClick={openCreate} className="bg-[#1565c0] hover:bg-[#0d47a1]" data-testid="banner-add-btn">
          <Plus size={14} className="me-1" />
          {t('شريحة جديدة', 'New Banner', 'Nouvelle bannière')}
        </Button>
      </div>

      {banners.length === 0 && (
        <Card className="p-8 text-center text-sm text-gray-500">
          {t('لا توجد شرائح بعد. اضغط على "شريحة جديدة" للبدء.', 'No banners yet. Click "New Banner" to add one.', 'Aucune bannière')}
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {banners.map((b) => (
          <Card key={b._id} className={`overflow-hidden ${!b.active ? 'opacity-60' : ''}`} data-testid={`banner-card-${b._id}`}>
            {b.image_url ? (
              <div className="aspect-[16/7] bg-gray-100 overflow-hidden">
                <img src={b.image_url} alt={b.title || ''} className="w-full h-full object-cover" />
              </div>
            ) : (
              <div className="aspect-[16/7] bg-gradient-to-br from-[#1976d2] to-[#0d47a1] flex items-center justify-center">
                <ImageIcon size={42} className="text-white/40" />
              </div>
            )}
            <div className="p-4">
              <h4 className="font-bold text-[#0d47a1]">{b.title || t('بدون عنوان', 'Untitled', 'Sans titre')}</h4>
              {b.subtitle && <p className="text-xs text-gray-500 mt-1">{b.subtitle}</p>}
              {b.cta_text && (
                <p className="text-xs text-blue-600 mt-1">→ {b.cta_text} ({b.cta_link || '#'})</p>
              )}
              <div className="flex items-center gap-2 mt-3">
                <span className="text-[10px] uppercase font-bold text-gray-400">#{b.order}</span>
                <Button size="sm" variant="outline" onClick={() => toggleActive(b)} data-testid={`banner-toggle-${b._id}`}>
                  {b.active ? <Eye size={14} /> : <EyeOff size={14} />}
                  <span className="ms-1">{b.active ? t('ظاهر', 'Visible', 'Visible') : t('مخفي', 'Hidden', 'Caché')}</span>
                </Button>
                <Button size="sm" variant="outline" onClick={() => openEdit(b)} data-testid={`banner-edit-${b._id}`}>
                  <Edit size={14} />
                </Button>
                <Button size="sm" variant="destructive" onClick={() => remove(b._id)} data-testid={`banner-delete-${b._id}`}>
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="banner-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0d47a1]">
              {editing ? t('تعديل شريحة', 'Edit Banner', 'Modifier') : t('شريحة جديدة', 'New Banner', 'Nouvelle bannière')}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            {/* Image picker */}
            <div>
              <Label>{t('الصورة', 'Image', 'Image')}</Label>
              <div className="mt-2 flex items-center gap-3">
                <div className="w-32 h-20 rounded-lg bg-gray-100 overflow-hidden flex items-center justify-center flex-shrink-0">
                  {form.image_url
                    ? <img src={form.image_url} alt="" className="w-full h-full object-cover" data-testid="banner-image-preview" />
                    : <ImageIcon size={28} className="text-gray-400" />}
                </div>
                <label className="cursor-pointer">
                  <Button type="button" variant="outline" asChild>
                    <span><Upload size={14} className="me-1" /> {t('رفع', 'Upload', 'Télécharger')}</span>
                  </Button>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleImage(e.target.files?.[0])}
                    data-testid="banner-image-input"
                  />
                </label>
              </div>
              <p className="text-xs text-gray-400 mt-1">{t('بحد أقصى 2 ميجابايت', 'Max 2 MB', 'Max 2 Mo')}</p>
            </div>

            <div>
              <Label>{t('العنوان', 'Title', 'Titre')}</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" data-testid="banner-title-input" />
            </div>
            <div>
              <Label>{t('العنوان الفرعي', 'Subtitle', 'Sous-titre')}</Label>
              <Textarea value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} rows={2} className="mt-1" data-testid="banner-subtitle-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t('نص الزر', 'Button Text', 'Texte du bouton')}</Label>
                <Input value={form.cta_text} onChange={(e) => setForm({ ...form, cta_text: e.target.value })} className="mt-1" data-testid="banner-cta-text-input" />
              </div>
              <div>
                <Label>{t('رابط الزر', 'Button Link', 'Lien')}</Label>
                <Input value={form.cta_link} onChange={(e) => setForm({ ...form, cta_link: e.target.value })} placeholder="/subscriptions" className="mt-1" data-testid="banner-cta-link-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 items-end">
              <div>
                <Label>{t('الترتيب', 'Order', 'Ordre')}</Label>
                <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value, 10) || 0 })} className="mt-1" data-testid="banner-order-input" />
              </div>
              <div className="flex items-center gap-2 pb-2">
                <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="banner-active-switch" />
                <span className="text-sm">{form.active ? t('ظاهرة', 'Visible', 'Visible') : t('مخفية', 'Hidden', 'Cachée')}</span>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>
                {t('إلغاء', 'Cancel', 'Annuler')}
              </Button>
              <Button onClick={save} disabled={saving} className="bg-[#1565c0] hover:bg-[#0d47a1]" data-testid="banner-save-btn">
                {t('حفظ', 'Save', 'Enregistrer')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBannersView;
